/**
 * Premium "Logo Stamp" intro.
 *
 * Visual design:
 *  - Heavy logo crashes down from above with motion blur, hits center and
 *    triggers a shockwave ring + screen shake + dust burst.
 *  - Title slides up beneath the logo, eyebrow fades in last.
 *  - Background is a deep cinematic look with blob drift.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, brandLogoImg, clipOutroFade, combineSceneFragments, escapeHtml,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const logoStamp: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  // Default ist absichtlich leer: das Brand-Logo-Bild enthält bereits den
  // Allyver-Schriftzug. Würden wir hier "Allyver" als Default rendern, hätten
  // wir den Brand-Namen doppelt im Bild ("Ghost-Text" direkt unter dem Logo).
  const headline = (inputs.text ?? "").trim();
  const sub = inputs.subtext ?? "";
  const headSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.62;
  const subSize = typeSize("md", ctx.format) * 0.55;
  const logoH = isVertical ? 220 : 170;

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const shakeId = `${clipPrefix}-shake`;
  const stampId = `${clipPrefix}-stamp`;
  const logoImgId = `${clipPrefix}-logoimg`;
  const ring1Id = `${clipPrefix}-ring1`;
  const ring2Id = `${clipPrefix}-ring2`;
  const dustId = `${clipPrefix}-dust`;
  const flashId = `${clipPrefix}-flash`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const eyeId = `${clipPrefix}-eye`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const logoMarkup = brandLogoImg({ id: logoImgId, sizePx: logoH, color: themeColors.fg });

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
  <div id="${shakeId}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:42px;padding:96px;will-change:transform;">

    <div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${subSize * 0.85}px;letter-spacing:8px;text-transform:uppercase;color:${accent};opacity:0;">— Welcome —</div>

    <div style="position:relative;display:flex;align-items:center;justify-content:center;">
      <div id="${ring1Id}" style="position:absolute;width:${logoH * 1.4}px;height:${logoH * 1.4}px;border-radius:9999px;border:3px solid ${accent};box-shadow:0 0 60px ${withAlpha(accent, 0.7)};opacity:0;"></div>
      <div id="${ring2Id}" style="position:absolute;width:${logoH * 1.4}px;height:${logoH * 1.4}px;border-radius:9999px;border:2px solid ${withAlpha(themeColors.fg, 0.6)};opacity:0;"></div>
      <div id="${dustId}" style="position:absolute;width:${logoH * 2.2}px;height:${logoH * 0.7}px;bottom:-${logoH * 0.5}px;background:radial-gradient(ellipse at 50% 100%, ${withAlpha(themeColors.fg, 0.32)} 0%, transparent 70%);filter:blur(14px);opacity:0;mix-blend-mode:screen;"></div>
      <div id="${stampId}" style="filter:drop-shadow(0 18px 50px ${withAlpha(accent, 0.6)});">
        ${logoMarkup}
      </div>
    </div>

    ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headSize}px;line-height:0.95;letter-spacing:-2.5px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 880 : 1400}px;text-shadow:0 0 60px ${withAlpha(accent, 0.5)};">${escapeHtml(headline)}</div>` : ""}
    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:0.4px;color:${withAlpha(themeColors.fg, 0.8)};text-align:center;max-width:${isVertical ? 760 : 1100}px;">${escapeHtml(sub)}</div>` : ""}
  </div>
  <div id="${flashId}" style="position:absolute;inset:0;background:${themeColors.fg};opacity:0;pointer-events:none;mix-blend-mode:screen;"></div>
</div>`;

  const init = [
    `gsap.set("#${stampId}", { y: -480, scale: 1.6, opacity: 0, filter: "blur(18px) drop-shadow(0 18px 50px ${withAlpha(accent, 0.6)})" });`,
    `gsap.set("#${ring1Id}", { scale: 0.4, opacity: 0 });`,
    `gsap.set("#${ring2Id}", { scale: 0.4, opacity: 0 });`,
    `gsap.set("#${dustId}", { opacity: 0, scaleX: 0.6 });`,
    headline ? `gsap.set("#${headId}", { opacity: 0, y: 40 });` : "",
    sub ? `gsap.set("#${subId}", { opacity: 0, y: 24 });` : "",
    `gsap.set("#${eyeId}", { opacity: 0, y: -16 });`,
  ].filter(Boolean).join("\n");

  const impact = 0.42;
  const tweens: string[] = [
    t.to(`#${stampId}`, { y: 0, scale: 1, opacity: 1, filter: `blur(0px) drop-shadow(0 18px 50px ${withAlpha(accent, 0.6)})`, duration: impact, ease: "raw:\"power4.in\"" }, 0),
    // Impact frame: flash + screen shake + ring burst.
    t.fromTo(`#${flashId}`, { opacity: 0 }, { opacity: 0.85, duration: 0.05, ease: "raw:\"power2.out\"" }, impact),
    t.to(`#${flashId}`, { opacity: 0, duration: 0.35, ease: "raw:\"power2.in\"" }, impact + 0.05),
    t.fromTo(`#${shakeId}`, { x: 0, y: 0 }, { x: 8, y: -6, duration: 0.05, ease: "raw:\"power2.out\"", yoyo: true, repeat: 5 }, impact),
    t.to(`#${stampId}`, { scale: 0.94, duration: 0.07, ease: "raw:\"power2.out\"" }, impact),
    t.to(`#${stampId}`, { scale: 1, duration: 0.35, ease: "raw:\"back.out(2.4)\"" }, impact + 0.07),
    // Ring shockwaves.
    t.to(`#${ring1Id}`, { scale: 2.6, opacity: 0, duration: 0.85, ease: "raw:\"power3.out\"" }, impact),
    t.fromTo(`#${ring1Id}`, { opacity: 0.9 }, { opacity: 0, duration: 0.85, ease: "raw:\"power2.out\"" }, impact),
    t.to(`#${ring2Id}`, { scale: 3.2, opacity: 0, duration: 1.05, ease: "raw:\"power3.out\"" }, impact + 0.07),
    t.fromTo(`#${ring2Id}`, { opacity: 0.6 }, { opacity: 0, duration: 1.05, ease: "raw:\"power2.out\"" }, impact + 0.07),
    // Dust kicks up.
    t.to(`#${dustId}`, { opacity: 1, scaleX: 2.4, duration: 0.5, ease: "raw:\"power2.out\"" }, impact + 0.02),
    t.to(`#${dustId}`, { opacity: 0, duration: 0.7, ease: "raw:\"power1.in\"" }, impact + 0.4),
  ];
  if (headline) {
    tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, impact + 0.22));
  }
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, impact + 0.42));
  tweens.push(t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, impact + 0.6));

  // Slow breathing zoom on the stamp.
  const dwell = Math.max(0.5, clipDurationSec - (impact + 1.1));
  tweens.push(t.to(`#${stampId}`, { scale: 1.04, duration: dwell, ease: "raw:\"sine.inOut\"" }, impact + 0.5));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
